import React from "react";
import { Link } from "react-router-dom";
import { Download, FileText, ShieldCheck } from "lucide-react";
import { API as API_BASE } from "../lib/api";

/**
 * Constitution — published CIO governing document of the
 * Ambedkarite Buddhist Global Network UK (charity registration in progress).
 *
 * Mirrors the Association CIO model constitution submitted to the
 * Charity Commission for England and Wales (Charities Act 2011).
 * 17 clauses + interpretation. Single source of truth — kept in sync
 * with /app/backend/governance_pack.py.
 */

const CLAUSES = [
  {
    n: "1",
    title: "Name",
    body:
      'The name of the Charitable Incorporated Organisation (the "CIO") is the ' +
      "Ambedkarite Buddhist Global Network UK.",
  },
  {
    n: "2",
    title: "National location of principal office",
    body: "The principal office of the CIO is in England and Wales.",
  },
  {
    n: "3",
    title: "Objects",
    body: "The objects of the CIO are, for the public benefit:",
    sub: [
      {
        t: "3.1 Advancement of the Buddhist religion",
        b:
          "particularly in the Ambedkarite tradition, for the public benefit in the " +
          "United Kingdom and internationally where consistent with the charity's " +
          "objects — through Dhamma teaching, meditation, retreats, festivals " +
          "(including Buddha Purnima, Ambedkar Jayanti and Dhamma Chakra Pravartan " +
          "Din), and public-facing publication and translation of Buddhist texts.",
      },
      {
        t: "3.2 Advancement of education",
        b:
          "for the public in Buddhist philosophy, ethics, equality, liberty, " +
          "fraternity, compassion and social wellbeing, including through " +
          "age-banded learning materials and structured online courses.",
      },
      {
        t: "3.3 Promotion of equality, inclusion and community cohesion",
        b:
          "for the public benefit, particularly among disadvantaged or " +
          "underserved communities — including caste-based discrimination as " +
          "recognised in the UK by virtue of the Equality Act 2010.",
      },
      {
        t: "3.4 Relief of those in need by reason of hardship",
        b:
          "the prevention and relief of poverty, sickness, bereavement, and " +
          "other forms of acute personal hardship affecting members of the " +
          "Ambedkarite Buddhist community in the United Kingdom and the wider " +
          "diaspora — including, where the trustees determine it serves the " +
          "public benefit, the making of confidential grants from a designated " +
          "compassion fund (the \"Karuṇā Fund\").",
      },
    ],
    note:
      "Nothing in this constitution authorises an application of the property " +
      "of the CIO for purposes which are not charitable in accordance with " +
      "section 2 of the Charities Act 2011.",
  },
  {
    n: "4",
    title: "Powers",
    body:
      "The CIO has power to do anything which is calculated to further its " +
      "objects or is conducive or incidental to doing so. In particular the " +
      "CIO has power to:",
    sub: [
      { t: "(a)", b: "raise funds (provided the CIO does not undertake any substantial permanent trading activity);" },
      { t: "(b)", b: "buy, sell, lease, mortgage or otherwise deal with any property;" },
      { t: "(c)", b: "borrow money and give security for loans;" },
      { t: "(d)", b: "employ paid or unpaid staff and engage volunteers (subject to clause 6);" },
      { t: "(e)", b: "deposit or invest funds and acquire investments in any manner;" },
      { t: "(f)", b: "co-operate with and support other charities and statutory bodies;" },
      { t: "(g)", b: "further its charitable purposes through physical, digital, hybrid and platform-based means — including charitable online teaching, meetings, educational programmes, religious services and public engagement — where this widens public access and improves public benefit;" },
      { t: "(h)", b: "operate, maintain and develop its own digital platform infrastructure as charitable mission infrastructure (and not as a commercial undertaking) in furtherance of the objects;" },
      { t: "(i)", b: "establish and operate designated funds — including a confidential compassion fund (the \"Karuṇā Fund\") — and make grants of money, goods or services to individuals and partner organisations in furtherance of object 3.4, subject to documented eligibility criteria, conflicts-of-interest controls, and trustee oversight;" },
      { t: "(j)", b: "do all such other lawful things as are necessary for the achievement of the objects." },
    ],
  },
  {
    n: "5",
    title: "Application of income and property",
    body:
      "The income and property of the CIO must be applied solely in promoting " +
      "the objects. A charity trustee is entitled to be reimbursed reasonable " +
      "expenses properly incurred when acting on behalf of the CIO. A charity " +
      "trustee may benefit from trustee indemnity insurance cover purchased at " +
      "the CIO's expense, in accordance with section 189 of the Charities Act 2011.",
  },
  {
    n: "6",
    title: "Benefits and payments to charity trustees and connected persons",
    body:
      "No charity trustee or connected person may buy or receive any goods or " +
      "services from the CIO on terms preferential to those applicable to " +
      "members of the public. No charity trustee may be employed by, or receive " +
      "any remuneration from, the CIO other than as expressly permitted by " +
      "this clause. No charity trustee or connected person may receive any " +
      "other financial benefit from the CIO unless authorised by the court or " +
      "the prior written consent of the Charity Commission has been obtained.",
  },
  {
    n: "7",
    title: "Conflicts of interest",
    body:
      "A charity trustee must declare the nature and extent of any interest, " +
      "direct or indirect, which they have in a proposed transaction or " +
      "arrangement with the CIO, and must absent themselves from any " +
      "discussion in which a conflict between their duty to the CIO and any " +
      "personal interest may arise. A trustee absenting themselves must not " +
      "vote or be counted in the quorum on the matter. (See the published " +
      "Conflict of Interest policy.)",
  },
  {
    n: "8",
    title: "Liability of members if the CIO is wound up",
    body:
      "If the CIO is wound up, the members of the CIO have no liability to " +
      "contribute to its assets and no personal responsibility for settling " +
      "its debts and liabilities.",
  },
  {
    n: "9",
    title: "Membership of the CIO",
    body:
      "Membership is open to any natural person aged 16 or over who supports " +
      "the objects of the CIO, has applied in the manner required by the " +
      "trustees, and has been approved by the trustees.",
    sub: [
      {
        t: "9.1 Subscription",
        b:
          "The trustees may invite an optional subscription as a supporter " +
          "contribution. Core public-benefit activities of the CIO remain " +
          "accessible to those who cannot afford a subscription, including " +
          "through hardship waivers granted confidentially by the trustees.",
      },
      {
        t: "9.2 Termination",
        b:
          "Membership terminates on death, written resignation, six months' " +
          "subscription arrears after a reasonable opportunity to pay, or " +
          "removal by trustee resolution on the ground that continued " +
          "membership is harmful to the CIO and the member has been given a " +
          "reasonable opportunity to be heard.",
      },
    ],
  },
  {
    n: "10",
    title: "Members' decisions",
    body:
      "Except for matters reserved by this constitution or by general law to " +
      "a particular procedure, any decision may be taken either by a vote at " +
      "a general meeting or by written resolution. A simple majority of votes " +
      "cast is required unless this constitution or the Charities Act 2011 " +
      "requires otherwise (e.g. dissolution or amendment of an entrenched " +
      "provision require a 75% majority).",
  },
  {
    n: "11",
    title: "General meetings of members",
    body:
      "The CIO must hold an Annual General Meeting in each calendar year; no " +
      "more than 15 months may elapse between successive AGMs. Notice of any " +
      "AGM or general meeting must be given to all members in writing at " +
      "least 14 clear days in advance. The quorum is 10% of the members " +
      "entitled to attend and vote, or three such members (whichever is the " +
      "greater).",
  },
  {
    n: "12",
    title: "Charity trustees",
    body:
      "The trustees are responsible for the general control and management of " +
      "the administration of the CIO. The minimum number of trustees is three. " +
      "The first charity trustees are:",
    sub: [
      { t: "12.1", b: "Mr Prafulla Ramteke — Founder Trustee / Chair." },
      { t: "12.2", b: "Miss Siddhi Dhiwar — Trustee / Secretary." },
      { t: "12.3", b: "Mr Vishwas Dhiwar — Trustee." },
    ],
    note:
      "Trustees are appointed by ordinary resolution of the members at an AGM, " +
      "or, between AGMs, by resolution of the existing trustees subject to " +
      "ratification at the next AGM. Every new trustee must be supplied with a " +
      "copy of this constitution and must sign a declaration of eligibility " +
      "and acceptance.",
  },
  {
    n: "13",
    title: "Retirement and removal of charity trustees",
    body:
      "A trustee ceases to hold office if they retire by written notice, " +
      "become disqualified under section 178 of the Charities Act 2011, cease " +
      "to be eligible under clause 13.2, or are removed by resolution of the " +
      "other trustees on the grounds set out below.",
    sub: [
      { t: "13.1 Eligibility", b: "Trustees must be aged 16 or over, not disqualified under section 178 of the Charities Act 2011, and must have signed a declaration of eligibility and acceptance." },
      { t: "13.2 Grounds for removal", b: "Include absence from three consecutive trustee meetings without prior consent, breach of the Code of Conduct, or failure to act in the best interests of the CIO. The trustee concerned must be given a reasonable opportunity to be heard." },
      { t: "13.3 Term", b: "Trustees are appointed for an initial term of three years and may be re-appointed for further three-year terms." },
    ],
  },
  {
    n: "14",
    title: "Trustee meetings",
    body:
      "The trustees must meet at least four times each year, with at least " +
      "seven days' notice (unless waived by all trustees). The quorum is two " +
      "trustees, or one-third of trustees in office (whichever is the greater). " +
      "Decisions are taken by simple majority of those present and voting; the " +
      "Chair has a casting vote. Trustees may participate by telephone or " +
      "video conference and are considered present at the meeting.",
  },
  {
    n: "15",
    title: "Delegation",
    body:
      "The trustees may delegate any of their powers or functions to a " +
      "committee, sub-committee, or to any individual trustee, employee or " +
      "volunteer, on such terms as they decide. The trustees must keep " +
      "written records of decisions taken under such delegation and remain " +
      "ultimately responsible for those decisions.",
  },
  {
    n: "16",
    title: "Amendment of constitution",
    body:
      "Subject to the following, this constitution may only be amended by " +
      "resolution of the members in general meeting passed by a 75% majority " +
      "of votes cast. Any amendment to the Objects clause (3), the " +
      "Application of Income and Property clause (5), the Benefits to " +
      "Trustees clause (6), this clause (16), or the Dissolution clause (17) " +
      "requires the prior written consent of the Charity Commission in " +
      "accordance with section 226 of the Charities Act 2011.",
  },
  {
    n: "17",
    title: "Voluntary winding up or dissolution",
    body:
      "The CIO may be wound up or dissolved by a resolution of the members " +
      "passed by a 75% majority of votes cast at a general meeting. On " +
      "dissolution, any net assets remaining after the satisfaction of debts " +
      "and liabilities must be applied or transferred to one or more other " +
      "charities with objects similar to those of the CIO. The trustees must " +
      "make all reasonable efforts to identify a suitable recipient charity.",
  },
  {
    n: "18",
    title: "Interpretation",
    body:
      'In this constitution: "connected person" means a person connected with ' +
      "a charity trustee within the meaning of section 188 of the Charities " +
      'Act 2011; "charity trustees" means the charity trustees for the time ' +
      'being of the CIO; "the Commission" means the Charity Commission for ' +
      'England and Wales; "member" and "membership" refer to membership of ' +
      'the CIO under clause 9; "the Act" means the Charities Act 2011.',
  },
];

function toRoman(num) {
  const map = [
    ["M", 1000], ["CM", 900], ["D", 500], ["CD", 400], ["C", 100],
    ["XC", 90], ["L", 50], ["XL", 40], ["X", 10], ["IX", 9],
    ["V", 5], ["IV", 4], ["I", 1],
  ];
  let n = parseInt(num, 10), out = "";
  for (const [r, v] of map) while (n >= v) { out += r; n -= v; }
  return out;
}

export default function Constitution() {
  return (
    <>
      <section className="bg-navy text-pearl py-20" data-testid="constitution-hero">
        <div className="section-container">
          <p className="eyebrow text-gold">Governance · Association CIO Constitution</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">Constitution</h1>
          <p className="mt-6 text-pearl/80 max-w-2xl">
            The governing document of the Ambedkarite Buddhist Global Network UK,
            adapted from the Charity Commission's model Association CIO
            constitution under the Charities Act 2011.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-pearl/10 border border-saffron/40 rounded-full text-xs">
            <ShieldCheck size={14} className="text-saffron" />
            <span className="text-pearl/85">
              Charity registration in progress &middot; CIO application in preparation
            </span>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={`${API_BASE}/governance/pack.pdf`}
              data-testid="constitution-download-pdf"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron-light"
            >
              <Download size={15} /> Download signed PDF
            </a>
            <a
              href={`${API_BASE}/governance/bundle.zip`}
              data-testid="constitution-download-bundle"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-pearl text-navy rounded-md text-sm font-medium hover:bg-pearl/90"
            >
              <Download size={15} /> Download review bundle (zip)
            </a>
            <a
              href={`${API_BASE}/governance/platform-fact-sheet.pdf`}
              data-testid="constitution-download-fact-sheet"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 text-pearl border border-pearl/25 rounded-md text-sm hover:bg-white/15"
            >
              <FileText size={15} /> Platform Fact Sheet
            </a>
            <Link
              to="/governance"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 text-pearl border border-pearl/25 rounded-md text-sm hover:bg-white/15"
            >
              <FileText size={15} /> Open Governance Book
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-navy/5">
        <div className="section-container max-w-3xl">
          <p className="eyebrow">Trustees as at this version</p>
          <h2 className="mt-2 font-display text-2xl text-navy">First charity trustees</h2>
          <ul className="mt-4 space-y-2 text-sm text-slate-700" data-testid="constitution-trustees">
            <li><strong className="text-navy">Mr Prafulla Ramteke</strong> — Founder Trustee / Chair.</li>
            <li><strong className="text-navy">Miss Siddhi Dhiwar</strong> — Trustee / Secretary.</li>
            <li><strong className="text-navy">Mr Vishwas Dhiwar</strong> — Trustee.</li>
          </ul>
          <p className="mt-4 text-xs text-slate-500 italic">
            All trustees serve voluntarily and receive no remuneration. Operational
            volunteer roles (Safeguarding Lead, Youth Lead, Education Lead,
            Community Outreach) are listed separately on the{" "}
            <Link to="/transparency" className="underline text-navy">Transparency page</Link>.
          </p>
        </div>
      </section>

      <section className="py-20 bg-pearl">
        <div className="section-container max-w-3xl">
          <div className="space-y-10">
            {CLAUSES.map((a) => (
              <article key={a.n} className="border-b border-navy/10 pb-8" data-testid={`article-${a.n}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-3xl text-gold min-w-16">{toRoman(a.n)}.</span>
                  <h2 className="font-display text-2xl sm:text-3xl text-navy">
                    Clause {a.n}. {a.title}
                  </h2>
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">{a.body}</p>
                {a.sub && (
                  <div className="mt-5 space-y-4 pl-5 border-l-2 border-gold/40">
                    {a.sub.map((s, i) => (
                      <div key={i}>
                        <h3 className="font-display text-lg text-navy">{s.t}</h3>
                        <p className="mt-1 text-slate-700 leading-relaxed">{s.b}</p>
                      </div>
                    ))}
                  </div>
                )}
                {a.note && (
                  <p className="mt-5 pl-5 text-sm text-slate-600 italic leading-relaxed border-l-2 border-saffron/40">
                    {a.note}
                  </p>
                )}
              </article>
            ))}
          </div>
          <p className="mt-14 text-xs text-slate-500 italic">
            This document forms the constitution of the Ambedkarite Buddhist
            Society UK and is maintained in the cloud, subject to amendment
            by the trustees under Clause 16 and (where required by section 226
            of the Charities Act 2011) with the prior written consent of the
            Charity Commission for England and Wales.
          </p>
        </div>
      </section>
    </>
  );
}
